<template>
  <div
    class="w-100"
    style="margin-bottom: 5px"
  >
    <div class="method-line d-flex justify-content-between align-items-center p-1">
      <div
        class="flex-grow-1 mx-1"
        :class="{ cancelled: isCancelled }"
      >
        <p class="m-0">
          {{ receipt.paymentMethod.name }}

          <span v-if="receipt.cardBrand">
            (
            <template v-if="receipt.paymentMethodInterface">
              {{ paymentMethodInterfaceLabel[receipt.paymentMethodInterface] || '-' }}
            </template>

            <template v-if="receipt.cardBrand">
              {{ receipt.cardBrand || '-' }}
            </template>
            )
          </span>
        </p>
        <p
          v-if="isPixTransactionFailed || receipt.nsu"
          class="m-0"
        >
          <b>NSU: </b> {{ receipt.nsu || '-' }}
        </p>

        <!--
          Parcelamento escolhido. Só aparece acima de uma parcela: em pagamento à vista a linha
          seria ruído, e é o caso da maioria das vendas.
        -->
        <p
          v-if="isInstallmentPayment"
          class="m-0"
        >
          <b>{{ $t('Parcelamento') }}: </b> {{ installmentLabel }}
        </p>

        <!--
          Acréscimo repassado ao cliente. Exibir aqui, e não só no momento da escolha, é o que
          permite ao operador conferir depois o que foi cobrado a mais — e ao cliente, no
          fechamento da venda.
        -->
        <!-- <p
          v-if="surchargeValue > 0"
          class="m-0"
        >
          <b>{{ $t('Acréscimo') }}: </b> {{ surchargeValue | currency(null, 2) }}
        </p> -->

      </div>

      <div
        class="mx-1 text-right"
        :class="{ cancelled: isCancelled }"
      >
        <p class="m-0">
          {{ value | currency }}
        </p>
        <p
          v-if="receipt.change > 0"
          class="m-0"
          :class="{
            'text-primary': !isCancelled,
            cancelled: isCancelled,
          }"
        >
          <b>{{ $t('Troco') }}: </b> {{ receipt.change | currency }}
        </p>
      </div>

      <div v-if="hasActions">
        <p class="mb-0 d-flex">
          <e-button
            v-if="showTefPrint"
            :id="`${$attrs.id}-btn_print`"
            :title="$t('PAY_BOX_SALE.TEF.PRINT_TEF')"
            variant="primary"
            icon="printer-fill"
            size="sm"
            @click="onPrint"
          />
          <e-button
            v-if="showCheckPaymentStatusBtn"
            :id="`${$attrs.id}-btn_check_payment`"
            :title="$t('PAY_BOX_SALE.TEF.CHECK_PAYMENT')"
            style="margin-left: 5px"
            variant="primary"
            icon="search"
            size="sm"
            @click="onCheckPayment"
          />
          <e-button
            v-if="showConfirmPaymentBtn"
            :id="`${$attrs.id}-btn_confirm_payment`"
            title="Confirmar pagamento"
            style="margin-left: 5px"
            variant="primary"
            icon="check2-circle"
            size="sm"
            @click="onConfirmPayment"
          />
          <e-button
            v-if="showBtnCancel"
            :id="`${$attrs.id}-btn_delete`"
            :title="isTEF ? $t('PAY_BOX_SALE.TEF.CANCEL_TEF') : $t('Excluir pagamento')"
            style="margin-left: 5px"
            tabindex="-1"
            variant="danger"
            size="sm"
            icon="trash"
            @click="onDelete"
          />
        </p>
      </div>
    </div>

    <div v-if="isPixTransactionFailed">
      <message-tef-pix-payment-error
        :is-atm="isAtm"
        class="method-line-message"
        :payment-data="{ saleUuid: currentSaleUuid }"
        :show-check-payment-status="showCheckPaymentStatusBtn"
        :show-confirm-payment="showConfirmPaymentBtn"
        :show-delete="showBtnCancel"
        @check-payment-status="onCheckPayment"
        @confirm-payment="onConfirmPayment"
        @delete="onDelete"
      />
    </div>
  </div>
</template>

<script>
import { paymentTypes } from '@/mixins'
import { isTefPix } from '@/store/utils/paybox/paybox-payments'
import EButton from '@/views/components/EButton.vue'
import { mapGetters } from 'vuex'
import MessageTefPixPaymentError from './MessageTefPixPaymentError.vue'

export default {
  components: { EButton, MessageTefPixPaymentError },
  mixins: [paymentTypes],
  props: {
    receipt: {
      type: Object,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
    hasActions: {
      type: Boolean,
      default: true,
    },
    isCancelled: {
      type: Boolean,
      default: false,
      required: false,
    },
    showBtnCanceled: {
      type: Boolean,
      default: true,
    },
    isAtm: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('pages/pdv/payBox', ['isChoppGiftCardConversion', 'currentSaleUuid']),
    ...mapGetters('pages/pdv/payBoxConfiguration', ['isTefPixCheckStatusEnabled']),
    isTEF() {
      return this.receipt.paymentMethodInterface === this.paymentMethodInterfaceEnum.TEF
    },

    /** Uma parcela é pagamento à vista: não há parcelamento a informar. */
    isInstallmentPayment() {
      return Number(this.receipt?.installmentCount || 1) > 1
    },

    surchargeValue() {
      return this.receipt?.surchargeValue || 0
    },

    /**
     * "6x de R$ 32,45 — total R$ 194,70".
     *
     * O valor da parcela vem GRAVADO no recebimento, calculado pelo rateio do servidor. Dividir o
     * total por N aqui daria outro número: o rateio trunca em duas casas e aloca o resto numa
     * parcela específica, para a soma fechar exata. Uma divisão simples produz dízima
     * (R$ 27,0417) e não corresponde ao que foi apresentado ao cliente.
     *
     * A divisão só entra como retaguarda para recebimentos gravados antes deste campo existir, e
     * ainda assim com duas casas: o filtro de moeda passa a exibir quatro quando o valor tem mais
     * de duas casas decimais.
     */
    installmentLabel() {
      const count = Number(this.receipt?.installmentCount || 1)
      const total = this.value || 0

      // Retaguarda para recebimentos sem o valor gravado — pedidos reabertos do servidor, por
      // exemplo. Trunca em duas casas em vez de dividir direto, que é a regra do rateio do
      // servidor para as parcelas regulares: a divisão pura produziria dízima (R$ 27,0417) e a
      // parcela exibida não bateria com a que o cliente viu.
      const truncated = count > 0 ? Math.floor((total / count) * 100) / 100 : total

      const perInstallment = this.receipt?.installmentValue || truncated

      const currency = amount => this.$options.filters.currency(amount, null, 2)

      return `${count}x ${this.$t('de')} ${currency(perInstallment)}`
    },




    showBtnCancel() {
      return this.showBtnCanceled && !this.isCancelled && !this.isChoppGiftCardConversion
    },

    showTefPrint() {
      return this.isTEF && !this.receipt?.tefTransactionFailed
    },

    isPixTransactionFailed() {
      return !this.isCancelled && this.receipt?.tefTransactionFailed && isTefPix(this.receipt)
    },
    showCheckPaymentStatusBtn() {
      return !!(
        this.isTefPixCheckStatusEnabled &&
        this.isPixTransactionFailed &&
        this.$listeners['click-check-payment'] &&
        this.receipt?.tefPayment?.nsuGateway
      )
    },
    showConfirmPaymentBtn() {
      return this.isPixTransactionFailed && !!this.$listeners['click-confirm-payment']
    },
  },

  methods: {
    onDelete() {
      this.$emit('click-delete')
    },
    onPrint() {
      this.$emit('click-print')
    },
    onCheckPayment() {
      this.$emit('click-check-payment')
    },
    onConfirmPayment() {
      this.$emit('click-confirm-payment')
    },
  },
}
</script>

<style lang="scss" scoped>
.method-line {
  background: #c4c4c4;
  color: var(--dark);
  font-weight: 600 !important;
}

.method-line-message {
  border-radius: 0px;
  border: 6px solid #c4c4c4;
}

.cancelled {
  text-decoration: line-through;
  color: var(--danger);
}
</style>
